import { PageFormValues } from './types'

/**
 * Shape of a single processed page profile as expected by the
 * `page_profiles` variable of the CREATE_PAGE / UPDATE_PAGE mutations
 * (GraphQL `PageProfileCustomInput`).
 */
export interface PageProfileCustomInput {
  id?: string
  language: string
  slug?: string
  title?: string
  subtitle?: string | null
  meta_description?: string | null
  cta_label?: string | null
  cta_link?: string | null
}

/**
 * Replicates the "auto-fill empty language profiles" transform that lived
 * inside PageForm's `onSubmit`.
 *
 * It finds the first profile that has BOTH title and slug filled, then for
 * every empty profile copies that filled profile's title/subtitle/meta_description
 * and sets `slug = ${filledProfile.slug}-${language.toLowerCase()}`. Non-empty
 * profiles are passed through (with subtitle/meta_description defaulted to null).
 *
 * Extracted so the new page-editing components (later stages) can share the
 * exact behavior PageForm used; PageForm itself is being removed in a later stage.
 *
 * @param profiles The form's `page_profiles` array.
 */
export function buildPageProfiles(
  profiles: PageFormValues['page_profiles']
): PageProfileCustomInput[] {
  // Find the first profile that has title and slug filled
  const filledProfile = profiles.find(
    p => p.title?.trim() && p.slug?.trim()
  )

  // Auto-fill empty profiles with the filled profile's data
  return profiles.map(p => {
    const hasContent =
      p.title?.trim() || p.slug?.trim() || p.cta_label?.trim() || p.cta_link?.trim()

    // If this profile is empty and we have a filled profile to copy from
    if (!hasContent && filledProfile) {
      // CTA is opt-in per language — an auto-filled profile doesn't inherit
      // the filled profile's CTA, it just gets none.
      return {
        id: p.id,
        language: p.language,
        title: filledProfile.title,
        subtitle: p.subtitle || filledProfile.subtitle || null,
        slug: `${filledProfile.slug}-${p.language.toLowerCase()}`,
        meta_description: p.meta_description || filledProfile.meta_description || null,
        cta_label: null,
        cta_link: null,
      }
    }

    return {
      id: p.id,
      language: p.language,
      slug: p.slug,
      title: p.title,
      subtitle: p.subtitle || null,
      meta_description: p.meta_description || null,
      cta_label: p.cta_label || null,
      cta_link: p.cta_link || null,
    }
  })
}
